/**
 * SettingsPage Component
 * Страница настроек приложения
 */

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import VKAccountsManager from '../VKAccountsManager';
import { validateCompanyInfo, validateINN, validateOGRN, validateBIK, validateAccount, validateRussianPhone, validateTelegram } from '../../utils/innValidator';
import './SettingsPage.css';

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyMessage, setCompanyMessage] = useState(null);
  
  // Данные компании
  const [companyInfo, setCompanyInfo] = useState({
    inn: '',
    name: '',
    type: 'ip',
    ogrn: '',
    address: '',
    phone: '',
    telegram: '',
    bank_name: '',
    bank_bik: '',
    bank_account: '',
    correspondent_account: ''
  });

  // Ошибки валидации
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadCompanyInfo();
  }, []);
  
  const loadCompanyInfo = async () => {
    setLoading(true);
    try {
      const response = await api.get('/company-info/');
      if (response.data) {
        setCompanyInfo({
          inn: response.data.inn || '',
          name: response.data.name || '',
          type: response.data.type || 'ip',
          ogrn: response.data.ogrn || '',
          address: response.data.address || '',
          phone: response.data.phone || '',
          telegram: response.data.telegram || '',
          bank_name: response.data.bank_name || '',
          bank_bik: response.data.bank_bik || '',
          bank_account: response.data.bank_account || '',
          correspondent_account: response.data.correspondent_account || ''
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки информации о компании:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner"></div>
        <p>Загрузка настроек...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Моя информация */}
      <div className="settings-card">
        <div className="card-header">
          <div className="card-icon">
            👤
          </div>
          <div className="card-title-group">
            <h2 className="card-title">Моя информация</h2>
            <p className="card-subtitle">Сведения о вашей компании для работы с VK ORD</p>
          </div>
        </div>
        
        <div className="company-form">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">ИНН <span className="required">*</span></label>
              <input
                type="text"
                className={`form-input ${validationErrors.inn ? 'error' : ''}`}
                value={companyInfo.inn}
                onChange={(e) => {
                  setCompanyInfo({...companyInfo, inn: e.target.value});
                  // Валидация при вводе
                  if (validationErrors.inn) {
                    const newErrors = {...validationErrors};
                    delete newErrors.inn;
                    setValidationErrors(newErrors);
                  }
                }}
                placeholder="1234567890"
                maxLength="12"
              />
              {validationErrors.inn && (
                <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                  ❌ {validationErrors.inn}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">ОГРН/ОГРНИП <span className="required">*</span></label>
              <input
                type="text"
                className={`form-input ${validationErrors.ogrn ? 'error' : ''}`}
                value={companyInfo.ogrn}
                onChange={(e) => {
                  setCompanyInfo({...companyInfo, ogrn: e.target.value});
                  if (validationErrors.ogrn) {
                    const newErrors = {...validationErrors};
                    delete newErrors.ogrn;
                    setValidationErrors(newErrors);
                  }
                }}
                placeholder="1234567890123"
                maxLength="15"
              />
              {validationErrors.ogrn && (
                <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                  ❌ {validationErrors.ogrn}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              ФИО или название <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-input ${validationErrors.name ? 'error' : ''}`}
              value={companyInfo.name}
              onChange={(e) => {
                setCompanyInfo({...companyInfo, name: e.target.value});
                if (validationErrors.name) {
                  const newErrors = {...validationErrors};
                  delete newErrors.name;
                  setValidationErrors(newErrors);
                }
              }}
              placeholder="Иванов Иван Иванович"
            />
            {validationErrors.name && (
              <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                ❌ {validationErrors.name}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Тип <span className="required">*</span>
            </label>
            <select
              className="form-input"
              value={companyInfo.type}
              onChange={(e) => setCompanyInfo({...companyInfo, type: e.target.value})}
            >
              <option value="ip">ИП</option>
              <option value="juridical">Юр. лицо</option>
              <option value="physical">Физ. лицо</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              Адрес, указанный в ЕГРЮЛ <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-input ${validationErrors.address ? 'error' : ''}`}
              value={companyInfo.address}
              onChange={(e) => {
                setCompanyInfo({...companyInfo, address: e.target.value});
                if (validationErrors.address) {
                  const newErrors = {...validationErrors};
                  delete newErrors.address;
                  setValidationErrors(newErrors);
                }
              }}
              placeholder="г. Москва, ул. Примерная, д. 1"
            />
            {validationErrors.address && (
              <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                ❌ {validationErrors.address}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Телефон</label>
              <input
                type="text"
                className={`form-input ${validationErrors.phone ? 'error' : ''}`}
                value={companyInfo.phone}
                onChange={(e) => {
                  setCompanyInfo({...companyInfo, phone: e.target.value});
                  if (validationErrors.phone) {
                    const newErrors = {...validationErrors};
                    delete newErrors.phone;
                    setValidationErrors(newErrors);
                  }
                }}
                placeholder="+79001234567"
              />
              {validationErrors.phone && (
                <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                  ❌ {validationErrors.phone}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">Телеграм</label>
              <input
                type="text"
                className={`form-input ${validationErrors.telegram ? 'error' : ''}`}
                value={companyInfo.telegram}
                onChange={(e) => {
                  setCompanyInfo({...companyInfo, telegram: e.target.value});
                  if (validationErrors.telegram) {
                    const newErrors = {...validationErrors};
                    delete newErrors.telegram;
                    setValidationErrors(newErrors);
                  }
                }}
                placeholder="@username"
              />
              {validationErrors.telegram && (
                <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                  ❌ {validationErrors.telegram}
                </div>
              )}
            </div>
          </div>

          <hr style={{margin: '16px 0', border: 'none', borderTop: '1px solid #e5e7eb'}} />
          
          <h3 style={{fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px'}}>
            Банковские реквизиты
          </h3>

          <div className="form-group">
            <label className="form-label">Банк</label>
            <input
              type="text"
              className="form-input"
              value={companyInfo.bank_name}
              onChange={(e) => setCompanyInfo({...companyInfo, bank_name: e.target.value})}
              placeholder="ПАО СБЕРБАНК"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">БИК</label>
              <input
                type="text"
                className={`form-input ${validationErrors.bank_bik ? 'error' : ''}`}
                value={companyInfo.bank_bik}
                onChange={(e) => {
                  setCompanyInfo({...companyInfo, bank_bik: e.target.value});
                  if (validationErrors.bank_bik) {
                    const newErrors = {...validationErrors};
                    delete newErrors.bank_bik;
                    setValidationErrors(newErrors);
                  }
                }}
                placeholder="044525225"
                maxLength="9"
              />
              {validationErrors.bank_bik && (
                <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                  ❌ {validationErrors.bank_bik}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">Расчётный счёт</label>
              <input
                type="text"
                className={`form-input ${validationErrors.bank_account ? 'error' : ''}`}
                value={companyInfo.bank_account}
                onChange={(e) => {
                  setCompanyInfo({...companyInfo, bank_account: e.target.value});
                  if (validationErrors.bank_account) {
                    const newErrors = {...validationErrors};
                    delete newErrors.bank_account;
                    setValidationErrors(newErrors);
                  }
                }}
                placeholder="40802810000000123456"
                maxLength="20"
              />
              {validationErrors.bank_account && (
                <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                  ❌ {validationErrors.bank_account}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Корр. счёт</label>
            <input
              type="text"
              className={`form-input ${validationErrors.correspondent_account ? 'error' : ''}`}
              value={companyInfo.correspondent_account}
              onChange={(e) => {
                setCompanyInfo({...companyInfo, correspondent_account: e.target.value});
                if (validationErrors.correspondent_account) {
                  const newErrors = {...validationErrors};
                  delete newErrors.correspondent_account;
                  setValidationErrors(newErrors);
                }
              }}
              placeholder="30101810400000000225"
              maxLength="20"
            />
            {validationErrors.correspondent_account && (
              <div style={{color: '#EF4444', fontSize: '12px', marginTop: '4px'}}>
                ❌ {validationErrors.correspondent_account}
              </div>
            )}
          </div>

          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#FEE2E2',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#991B1B'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>❌ Ошибки валидации:</div>
              {Object.entries(validationErrors).map(([field, error]) => (
                <div key={field} style={{ marginLeft: '8px', fontSize: '14px' }}>
                  • {error}
                </div>
              ))}
            </div>
          )}

          {companyMessage && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: companyMessage.type === 'success' ? '#D1FAE5' : '#FEE2E2',
              border: `1px solid ${companyMessage.type === 'success' ? '#10B981' : '#EF4444'}`,
              borderRadius: '8px',
              color: companyMessage.type === 'success' ? '#065F46' : '#991B1B'
            }}>
              {companyMessage.type === 'success' ? '✅' : '❌'} {companyMessage.text}
            </div>
          )}

          <div style={{marginTop: '16px'}}>
            <button 
              className="save-button"
              onClick={async () => {
                // Полная валидация перед сохранением
                const validation = validateCompanyInfo(companyInfo);
                
                if (!validation.isValid) {
                  setValidationErrors(validation.errors);
                  setCompanyMessage({ type: 'error', text: 'Исправьте ошибки в форме перед сохранением' });
                  return;
                }

                setValidationErrors({});
                setSaving(true);
                try {
                  await api.post('/company-info/', companyInfo);
                  setCompanyMessage({ type: 'success', text: 'Информация о компании сохранена' });
                  setTimeout(() => setCompanyMessage(null), 3000);
                } catch (error) {
                  setCompanyMessage({ type: 'error', text: 'Ошибка сохранения: ' + (error.response?.data?.detail || error.message) });
                  setTimeout(() => setCompanyMessage(null), 5000);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              {saving ? 'Сохранение...' : 'Сохранить информацию'}
            </button>
          </div>
        </div>
      </div>

      {/* VK Accounts Manager Section */}
      <div className="settings-card vk-accounts-section">
        <div className="card-header">
          <div className="card-title-group">
            <h2 className="card-title">VK ОРД Кабинеты</h2>
            <p className="card-subtitle">
              Управление несколькими рекламными кабинетами VK ОРД
            </p>
          </div>
        </div>
        
        <VKAccountsManager 
          onAccountSwitch={() => {
            // Обновление данных после переключения кабинета
            window.location.reload();
          }}
        />
      </div>
    </div>
  );
};

export default SettingsPage;
